// Pôle « Contrôle » de la DGH (charte D-038, « exceptions d'abord ») : en tête, la liste numérotée de ce qui
// demande une action dans le périmètre (incidents majeurs, délais de correction dépassés, rapports à
// examiner, signalements à qualifier, observations de prix à instruire) ; puis la synthèse des inspections,
// incidents, constats, prix et observations, chacune ouvrant son écran. Les anomalies et rapprochements (G1)
// reposent sur les déclarations et livraisons des lots 2 et 3 : leur bloc l'indique, sans rien simuler.

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { aUnRole, useSession } from '../../app/session';
import { Indisponible, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { date, jourLong, libelleProvince } from '../../composants/format';
import { delaiDepasse, libelleDans, useConstatsASuivre, useListeInspections, useReferentielInspections } from '../inspections/api';
import { libelleIncident, useIncidents, useIncidentsMajeurs, useReferentielIncidents } from '../incidents/Incidents';
import '../inspections/terrain.css';

interface Observation {
  id: string;
  etat: string;
  installationNom: string | null;
  prixOfficielIllustratif: boolean | null;
}

interface PointControle {
  cle: string;
  ton: 'rouge' | 'ambre';
  marque: string;
  titre: string;
  detail: string;
  lien: string;
}

const ROLES_PLANIFICATION = ['administrateur_national', 'directeur_general', 'directeur', 'chef_service', 'superviseur'];

export function PoleControle() {
  const { etat } = useSession();
  const inspections = useListeInspections();
  const refInspections = useReferentielInspections();
  const constats = useConstatsASuivre();
  const incidents = useIncidents();
  const majeurs = useIncidentsMajeurs();
  const refIncidents = useReferentielIncidents();
  const observations = useQuery({ queryKey: ['prix', 'observations', ''], queryFn: async () => (await api<Observation[]>('/api/v1/observations-prix')).donnees, retry: false });
  const grille = useQuery({ queryKey: ['prix', 'grille'], queryFn: async () => api<unknown[]>('/api/v1/prix'), retry: false });
  const peutPlanifier = aUnRole(etat, ...ROLES_PLANIFICATION);
  const provinces = [...new Set(etat?.affectations?.flatMap((a) => a.perimetre.provinces) ?? [])];
  const perimetre = provinces.length ? `périmètre ${provinces.map(libelleProvince).join(', ')}` : 'périmètre national';

  const chargement = inspections.isLoading || incidents.isLoading || majeurs.isLoading;
  const listeInspections = inspections.data ?? [];
  const compte = (e: string) => listeInspections.filter((i) => i.etat === e).length;
  const listeConstats = constats.data ?? [];
  const enRetard = listeConstats.filter(delaiDepasse);
  const listeIncidents = incidents.data ?? [];
  const signales = listeIncidents.filter((i) => i.etat === 'signale');
  const listeObservations = observations.data ?? [];
  const aInstruire = listeObservations.filter((o) => ['signalee', 'en_instruction', 'confirmee'].includes(o.etat));

  const points: PointControle[] = [];
  for (const i of (majeurs.data?.incidents ?? []).filter((x) => x.gravite >= 4)) {
    points.push({ cle: i.id, ton: 'rouge', marque: `${libelleIncident(refIncidents.data?.gravites, i.gravite)}, en cours`, titre: `${libelleIncident(refIncidents.data?.types, i.type)}, ${i.installationNom ?? i.lieu}`, detail: `${i.reference}, ${libelleProvince(i.provinceCode)}`, lien: `/incidents/${i.id}` });
  }
  if (enRetard.length > 0)
    points.push({ cle: 'retard', ton: 'rouge', marque: 'Corrections, délai dépassé', titre: `${formaterNombre(enRetard.length)} constat${enRetard.length > 1 ? 's' : ''} non corrigé${enRetard.length > 1 ? 's' : ''} dans le délai demandé`, detail: 'Suivi à assurer avec les organisations inspectées', lien: '#constats' });
  for (const i of (majeurs.data?.incidents ?? []).filter((x) => x.gravite === 3)) {
    points.push({ cle: i.id, ton: 'ambre', marque: `${libelleIncident(refIncidents.data?.gravites, i.gravite)}, ${libelleIncident(refIncidents.data?.etats, i.etat).toLowerCase()}`, titre: `${libelleIncident(refIncidents.data?.types, i.type)}, ${i.installationNom ?? i.lieu}`, detail: `${i.reference}, ${libelleProvince(i.provinceCode)}`, lien: `/incidents/${i.id}` });
  }
  if (compte('transmise') > 0)
    points.push({ cle: 'transmises', ton: 'ambre', marque: 'Inspections, examen en attente', titre: `${formaterNombre(compte('transmise'))} rapport${compte('transmise') > 1 ? 's' : ''} transmis à examiner`, detail: 'L’examen rend le rapport visible de l’organisation inspectée', lien: '/inspections?etat=transmise' });
  const signalesMineurs = signales.filter((i) => i.gravite < 3);
  if (signalesMineurs.length > 0)
    points.push({ cle: 'signales', ton: 'ambre', marque: 'Incidents, qualification en attente', titre: `${formaterNombre(signalesMineurs.length)} signalement${signalesMineurs.length > 1 ? 's' : ''} à qualifier`, detail: 'Gravité et type à confirmer', lien: '/incidents?etat=signale' });
  if (aInstruire.length > 0)
    points.push({ cle: 'observations', ton: 'ambre', marque: 'Prix, observations à instruire', titre: `${formaterNombre(aInstruire.length)} observation${aInstruire.length > 1 ? 's' : ''} de prix en attente de décision`, detail: 'Instruction par une personne habilitée, aucune qualification automatique', lien: '/prix?onglet=observations' });

  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="contexte">
            {jourLong()}, {perimetre}
          </span>
          <h1>{chargement ? 'Contrôle' : points.length === 0 ? 'Rien n’attend une action du contrôle.' : `${formaterNombre(points.length)} point${points.length > 1 ? 's' : ''} à traiter.`}</h1>
        </div>
        {peutPlanifier && (
          <Link className="bouton primaire" to="/inspections/planifier">
            <Icone nom="ajout" />
            Planifier une inspection
          </Link>
        )}
      </div>

      {chargement && <Squelette lignes={6} libelle="Chargement du contrôle" />}
      {inspections.error && <MessageErreur erreur={inspections.error} titre="Inspections indisponibles" />}
      {!chargement && points.length > 0 && (
        <section className="a-traiter" aria-labelledby="titre-points">
          <h2 id="titre-points" className="sur-titre">
            Ce qui demande une action
          </h2>
          <ol className="liste-exceptions">
            {points.map((p, i) => {
              const contenu = (
                <>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{p.marque}</span>
                    <b>{p.titre}</b>
                    <small>{p.detail}</small>
                  </span>
                  <Icone nom="chevron" />
                </>
              );
              return (
                <li key={p.cle}>
                  {p.lien.startsWith('#') ? (
                    <a className={`exception ${p.ton}`} href={p.lien}>
                      {contenu}
                    </a>
                  ) : (
                    <Link className={`exception ${p.ton}`} to={p.lien}>
                      {contenu}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <div className="grille-controle">
        <section className="carte-bloc" aria-labelledby="titre-anomalies">
          <h2 id="titre-anomalies">Anomalies et rapprochements</h2>
          <Indisponible motif="les contrôles d’écarts (chargé contre reçu, déclaré contre mesuré, théorique contre inventaire) portent sur les déclarations, livraisons et rapprochements des lots 2 et 3, pas encore en service" />
        </section>

        <section className="carte-bloc" aria-labelledby="titre-synthese-inspections">
          <div className="carte-bloc-tete">
            <h2 id="titre-synthese-inspections">Inspections</h2>
            <Link to="/inspections">Toutes</Link>
          </div>
          <dl className="paires">
            {['planifiee', 'en_cours', 'transmise', 'examinee', 'cloturee'].map((e) => (
              <div key={e}>
                <dt>{libelleDans(refInspections.data?.etats, e)}</dt>
                <dd>{inspections.data ? formaterNombre(compte(e)) : <Indisponible motif="liste non lisible" />}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="carte-bloc" aria-labelledby="titre-synthese-incidents">
          <div className="carte-bloc-tete">
            <h2 id="titre-synthese-incidents">Incidents</h2>
            <Link to="/incidents">Registre</Link>
          </div>
          <dl className="paires">
            <div>
              <dt>Majeurs en cours (gravité 3 et 4)</dt>
              <dd>{majeurs.data ? formaterNombre(majeurs.data.nombre) : <Indisponible motif="non lisible" />}</dd>
            </div>
            {['signale', 'qualifie', 'en_traitement', 'cloture'].map((e) => (
              <div key={e}>
                <dt>{libelleIncident(refIncidents.data?.etats, e)}</dt>
                <dd>{incidents.data ? formaterNombre(listeIncidents.filter((i) => i.etat === e).length) : <Indisponible motif="non lisible" />}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="carte-bloc" aria-labelledby="titre-synthese-prix">
          <div className="carte-bloc-tete">
            <h2 id="titre-synthese-prix">Prix et observations</h2>
            <Link to="/prix">Ouvrir</Link>
          </div>
          <dl className="paires">
            {[
              ['signalee', 'Signalées'],
              ['en_instruction', 'En instruction'],
              ['confirmee', 'Confirmées, à transmettre'],
              ['infirmee', 'Infirmées'],
              ['transmise', 'Transmises'],
            ].map(([e, l]) => (
              <div key={e}>
                <dt>{l}</dt>
                <dd>{observations.data ? formaterNombre(listeObservations.filter((o) => o.etat === e).length) : <Indisponible motif="non lisible" />}</dd>
              </div>
            ))}
          </dl>
          {grille.data?.meta.illustratif === true && (
            <p className="espace-haut">
              <span className="mention-illustrative">Prix de référence illustratifs</span> <span className="attenue">jeu de données fictif, sans valeur officielle</span>
            </p>
          )}
        </section>
      </div>

      <section className="carte-bloc espace-haut" id="constats" aria-labelledby="titre-constats">
        <div className="carte-bloc-tete">
          <h2 id="titre-constats">Constats à suivre</h2>
          <span className="attenue">
            {constats.data && listeConstats.length > 0 ? `${formaterNombre(listeConstats.length)} ouvert${listeConstats.length > 1 ? 's' : ''} ou en correction, ${formaterNombre(enRetard.length)} en retard` : ''}
          </span>
        </div>
        {constats.isLoading && <Squelette lignes={3} />}
        {constats.error && <MessageErreur erreur={constats.error} titre="Constats indisponibles" />}
        {constats.data && listeConstats.length === 0 && <p className="attenue">Aucun constat ouvert dans votre périmètre.</p>}
        {constats.data && listeConstats.length > 0 && (
          <div className="tableau-conteneur">
            <table className="tableau lignes-48">
              <caption className="visuellement-cache">Constats à suivre</caption>
              <thead>
                <tr>
                  <th scope="col">Constat</th>
                  <th scope="col" className="masquable">
                    Inspection
                  </th>
                  <th scope="col">Délai</th>
                  <th scope="col">État</th>
                </tr>
              </thead>
              <tbody>
                {listeConstats.slice(0, 12).map((c) => {
                  const retard = delaiDepasse(c);
                  return (
                    <tr key={c.id}>
                      <td>
                        <Link className="lien-ligne" to={`/inspections/${c.inspectionId}`}>
                          {libelleDans(refInspections.data?.categoriesConstats, c.categorie)}
                        </Link>
                        <small>{c.installationNom}</small>
                      </td>
                      <td className="masquable">
                        {c.inspectionReference}
                        <small>constat {c.numero}</small>
                      </td>
                      <td>{c.delai ? date(c.delai) : 'Sans délai'}</td>
                      <td>
                        <span className={retard ? 'etiquette rouge' : c.etat === 'correction_declaree' ? 'etiquette ambre' : 'etiquette'}>{retard ? 'Délai dépassé' : libelleDans(refInspections.data?.etatsConstats, c.etat)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
